export type DateOnlyParts = {
  year: number
  month: number
  day: number
}

export type PeriodMode = 'last' | 'from'
export type PeriodUnit = 'week' | 'month' | 'year'

const MELBOURNE_TIME_ZONE = 'Australia/Melbourne'

const padTwo = (value: number) => String(value).padStart(2, '0')

const toDateOnlyString = ({ year, month, day }: DateOnlyParts) =>
  `${year}-${padTwo(month)}-${padTwo(day)}`

const toUtcDate = ({ year, month, day }: DateOnlyParts) => new Date(Date.UTC(year, month - 1, day))

const fromUtcDate = (date: Date): DateOnlyParts => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
})

const getDatePartsInTimeZone = (date: Date, timeZone: string): DateOnlyParts => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  const year = Number(parts.find(part => part.type === 'year')?.value)
  const month = Number(parts.find(part => part.type === 'month')?.value)
  const day = Number(parts.find(part => part.type === 'day')?.value)

  if (!year || !month || !day) {
    throw new Error('Unable to resolve Melbourne date')
  }

  return { year, month, day }
}

const getLocalDateParts = (date: Date): DateOnlyParts => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
})

const addDays = (dateParts: DateOnlyParts, days: number): DateOnlyParts => {
  const date = toUtcDate(dateParts)
  date.setUTCDate(date.getUTCDate() + days)
  return fromUtcDate(date)
}

const shiftMonth = (dateParts: DateOnlyParts, months: number): DateOnlyParts => {
  const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1 + months, 1))
  return fromUtcDate(date)
}

const startOfWeekMonday = (dateParts: DateOnlyParts): DateOnlyParts => {
  const dayOfWeek = toUtcDate(dateParts).getUTCDay()
  const daysSinceMonday = (dayOfWeek + 6) % 7
  return addDays(dateParts, -daysSinceMonday)
}

const endOfWeekMonday = (dateParts: DateOnlyParts): DateOnlyParts =>
  addDays(startOfWeekMonday(dateParts), 6)

const startOfMonthDate = (dateParts: DateOnlyParts): DateOnlyParts => ({
  year: dateParts.year,
  month: dateParts.month,
  day: 1,
})

const compareDateOnly = (left: DateOnlyParts, right: DateOnlyParts) =>
  toDateOnlyString(left).localeCompare(toDateOnlyString(right))

export const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime())

export const formatCalendarInputDate = (date: Date) => {
  if (!isValidDate(date)) {
    return ''
  }
  const { day, month, year } = getLocalDateParts(date)
  return `${padTwo(day)}/${padTwo(month)}/${String(year).slice(-2)}`
}

export const computeDashboardDateRange = ({
  periodMode,
  periodCount,
  periodUnit,
  fromDate,
  toDate,
  today = new Date(),
  timeZone = MELBOURNE_TIME_ZONE,
}: {
  periodMode: PeriodMode
  periodCount: string
  periodUnit: PeriodUnit
  fromDate: Date | null
  toDate: Date | null
  today?: Date
  timeZone?: string
}) => {
  if (periodMode === 'from') {
    if (!fromDate || !toDate || !isValidDate(fromDate) || !isValidDate(toDate)) {
      throw new Error('Both From and To dates are required')
    }

    const fromDateParts = getLocalDateParts(fromDate)
    const toDateParts = getLocalDateParts(toDate)
    const todayInMelbourne = getDatePartsInTimeZone(today, timeZone)

    if (compareDateOnly(fromDateParts, toDateParts) > 0) {
      throw new Error('From date must be on or before To date')
    }

    if (
      compareDateOnly(fromDateParts, todayInMelbourne) > 0 ||
      compareDateOnly(toDateParts, todayInMelbourne) > 0
    ) {
      throw new Error('Future dates are not allowed')
    }

    return {
      startDate: toDateOnlyString(fromDateParts),
      endDate: toDateOnlyString(toDateParts),
    }
  }

  const count = Math.max(1, parseInt(periodCount, 10) || 1)
  const todayInMelbourne = getDatePartsInTimeZone(today, timeZone)

  let start: DateOnlyParts
  let end: DateOnlyParts

  if (periodUnit === 'week') {
    end = endOfWeekMonday(todayInMelbourne)
    start = addDays(startOfWeekMonday(todayInMelbourne), -7 * (count - 1))
  } else if (periodUnit === 'month') {
    end = todayInMelbourne
    start = startOfMonthDate(shiftMonth(todayInMelbourne, -(count - 1)))
  } else {
    end = todayInMelbourne
    start = startOfMonthDate(shiftMonth(todayInMelbourne, -(12 * count - 1)))
  }

  return {
    startDate: toDateOnlyString(start),
    endDate: toDateOnlyString(end),
  }
}
